// Renderer mirror of the main-process snooze (Updater.snoozedVersion / snoozedUntil).
// Main is the source of truth (survives renderer reload, dies with the app);
// this store exists so ProxyWatchdog can subscribe with useSyncExternalStore.
//
// Per rule: think-in-systems (ai.knowledge/global) — one signal per job.
// Per rule: investigate-before-adding (ai.knowledge/global) — sibling of update-check-status.ts.

import { isElectronApp } from './utils.js';

export interface DesktopUpdateSnooze {
  version: string;
  until: number /* epoch ms */;
}

export interface DesktopUpdateSnoozeState {
  known: boolean;
  snooze: DesktopUpdateSnooze | null;
}

// keep in sync with UPDATE_SNOOZE_MS (electron main)
export const DESKTOP_UPDATE_SNOOZE_MS = 24 * 60 * 60 * 1000;

// The mirror is unknown until the first main snapshot. An unknown mirror must
// not be read as "not snoozed": a stale UPDATE_AVAILABLE in state-sync would
// re-nag a version the user dismissed. Per rule: absent-data-gates-permissively
// (ai.knowledge/global) — explicit unknown|loaded tri-state; suppressing a
// toast while unknown is the permissive direction for a notification.
let known = false;
let snooze: DesktopUpdateSnooze | null = null;
let snapshot: DesktopUpdateSnoozeState = { known: false, snooze: null };
const listeners = new Set<() => void>();

if (!isElectronApp()) {
  known = true;
  snapshot = { known: true, snooze: null };
}

function snoozeEqual(a: DesktopUpdateSnooze | null, b: DesktopUpdateSnooze | null): boolean {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  return a.version === b.version && a.until === b.until;
}

export function setDesktopUpdateSnooze(next: DesktopUpdateSnooze | null): void {
  if (known && snoozeEqual(snooze, next)) {
    return;
  }
  snooze = next;
  known = true;
  snapshot = { known, snooze };
  listeners.forEach((fn) => fn());
}

export function getDesktopUpdateSnooze(): DesktopUpdateSnooze | null {
  return snooze;
}

export function isDesktopUpdateSnoozeKnown(): boolean {
  return known;
}

export function getDesktopUpdateSnoozeState(): DesktopUpdateSnoozeState {
  return snapshot;
}

export function subscribeDesktopUpdateSnooze(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True only for the snoozed version, before its expiry. */
export function isVersionSnoozed(
  latestVersion: string | undefined,
  current: DesktopUpdateSnooze | null,
  now: number
): boolean {
  return (
    !!latestVersion && current !== null && current.version === latestVersion && now < current.until
  );
}
